import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Gate for role-scoped routes. Wraps routes that require the user to hold one of
 * the given roles. Registered AFTER the public routes (login/register) so
 * anonymous users are redirected to sign in rather than shown a 403.
 *
 *   <Route element={<RoleRoute allow={['epc_installer', 'operator', 'admin']} />}>
 *     <Route path="/site-registry" element={<SiteRegistry />} />
 *   </Route>
 *
 * - Not authenticated  → redirect to /login, preserving the intended destination.
 * - Authenticated, wrong role → redirect to /403 (themed Forbidden page).
 * - `admin` is always allowed (platform superuser).
 */
export default function RoleRoute({ allow = [] }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5" style={{ paddingTop: '80px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const roles = Array.isArray(allow) ? allow : [allow];
  const permitted = user?.role === 'admin' || roles.length === 0 || roles.includes(user?.role);

  if (!permitted) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
